import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { getTypeNeedSelectList } from "@/api";
import { Chip } from "@/components/kit";

import type { TypeNeedSelectItem } from "@/types";

/** "Barchasi" yoki TypeNeed id (1, 2, ...) */
export type TypeNeedFilterValue = "barchasi" | number;

export interface FilterTabItem {
  value: TypeNeedFilterValue;
  label: string;
}

interface FilterTabsProps {
  activeFilter: TypeNeedFilterValue;
  onFilterChange: (filter: TypeNeedFilterValue) => void;
  /** Value 1 (Shoshilinch) tabida ko'rsatiladigan badge soni */
  urgentCount?: number;
}

export function FilterTabs({
  activeFilter,
  onFilterChange,
  urgentCount = 0,
}: FilterTabsProps) {
  const { t, i18n } = useTranslation();
  // API dan kelgan tablar (til-neytral id + label) — alohida saqlanadi
  const [apiItems, setApiItems] = useState<TypeNeedSelectItem[]>([]);
  const [loading, setLoading] = useState(true);

  // i18n.language o'zgarganda qayta yuklash — t funksiyasi emas!
  // t har renderdа reference o'zgartirishi mumkin → keraksiz so'rovlar + CORS
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTypeNeedSelectList()
      .then((list) => {
        if (!cancelled) setApiItems(list);
      })
      .catch(() => {
        if (!cancelled) setApiItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  // "Barchasi" labeli render paytida t() dan olinadi — alohida so'rov kerak emas
  const tabs: FilterTabItem[] = [
    { value: "barchasi", label: t("filterAll") },
    ...apiItems.map((item) => ({ value: item.value as number, label: item.text })),
  ];

  if (loading && tabs.length === 0) {
    return (
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeleton h-9 w-24 rounded-xl shrink-0" />
        ))}
      </div>
    );
  }

  if (!loading && tabs.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.15 }}
      className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1"
      style={{ scrollbarWidth: "none" }}
      role="tablist"
      aria-label={t("filterCategoryAria")}
    >
      {tabs.map((tab) => {
        const isActive = activeFilter === tab.value;
        const showCount = tab.value === 1 && urgentCount > 0;
        return (
          <Chip
            key={String(tab.value)}
            selected={isActive}
            onClick={() => onFilterChange(tab.value)}
            count={showCount ? urgentCount : undefined}
          >
            {tab.label}
          </Chip>
        );
      })}
    </motion.div>
  );
}
